import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { metaTrackContact } from './analytics/metaPixel';
import './ChatbotWidget.css';

const GOOGLE_AI_API_KEY = process.env.REACT_APP_GOOGLE_AI_API_KEY;
const MODEL_ID = 'gemini-2.5-flash';
const MODEL_ENDPOINT = `https://generativelanguage.googleapis.com/v1/models/${MODEL_ID}:generateContent`;

const BASE_INSTRUCTIONS = [
  'You are the AI assistant for an online store that sells lifting straps.',
  'Match the user language (Bulgarian or English) and keep answers short and clear.',
  'Explain product differences (Фитили RP Versa Grips style 19.99 €, colors green or gray, Girlfriend version 13.99 €), shipping rules, and how to buy for a gym buddy.',
  'Shipping rules: free delivery when buying 2 or more items in one order; otherwise delivery is up to 3.99 €.',
  'If the user asks about sizes, explain one-size unisex. For the RP Versa Grips style product, color is green or gray.',
  'If you are not sure about something, say that the information is not available on the site.',
  'Avoid marketing fluff; answer in 3–6 short lines maximum.'
].join('\n');

const MAX_CONTEXT_LENGTH = 20000;
const MAX_HISTORY_LENGTH = 8;

const initialMessages = [
  {
    role: 'model',
    content: "Здравей! Аз съм асистентът за lifting straps. Попитай ме за продуктите, доставката или кой сет е по-добър за теб и твоя gym buddy."
  }
];

const ChatbotWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [siteContext, setSiteContext] = useState('');
  const messagesContainerRef = useRef(null);

  const collectSiteContent = () => {
    if (typeof document === 'undefined') return '';

    const sections = Array.from(
      document.querySelectorAll('main, section, article, header, footer, [data-section], .section')
    );

    const sectionSummaries = sections
      .map((element, index) => {
        const titleCandidate =
          element.getAttribute('aria-label') ||
          element.dataset?.sectionTitle ||
          element.querySelector('h1, h2, h3, h4')?.innerText ||
          element.id ||
          `Section ${index + 1}`;

        const textContent = element.innerText.replace(/\s+/g, ' ').trim();
        if (!textContent) return null;
        return `### ${titleCandidate}\n${textContent}`;
      })
      .filter(Boolean)
      .join('\n\n');

    const rawContent = sectionSummaries || '';
    return rawContent.slice(0, MAX_CONTEXT_LENGTH);
  };

  useEffect(() => {
    const compiled = collectSiteContent();
    if (compiled) {
      setSiteContext(compiled);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const compiled = collectSiteContent();
    if (compiled) {
      setSiteContext(compiled);
    }
  }, [isOpen]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages]);

  const trimmedHistory = useMemo(() => {
    if (messages.length <= MAX_HISTORY_LENGTH) return messages;
    return messages.slice(messages.length - MAX_HISTORY_LENGTH);
  }, [messages]);

  const detectLanguage = (text) => (/[а-яА-Я]/.test(text) ? 'bg' : 'en');

  const sendMessage = async () => {
    const outgoing = inputValue;
    if (!outgoing.trim() || isLoading) return;

    if (!GOOGLE_AI_API_KEY) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          content:
            detectLanguage(outgoing) === 'bg'
              ? 'Чатботът не е конфигуриран правилно. Липсва Google AI API key в средата (REACT_APP_GOOGLE_AI_API_KEY).'
              : 'Chatbot is not configured correctly. Missing Google AI API key in environment (REACT_APP_GOOGLE_AI_API_KEY).'
        }
      ]);
      return;
    }

    const userMessage = {
      role: 'user',
      content: outgoing.trim()
    };

    const detectedLanguage = detectLanguage(userMessage.content);

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    if (!messages.some((m) => m.role === 'user')) {
      metaTrackContact();
    }

    const conversationForRequest = [...trimmedHistory, userMessage];

    const contents = [
      {
        role: 'user',
        parts: [
          {
            text: `${BASE_INSTRUCTIONS}\n\nSite context (truncated):\n${siteContext || 'No extra site text captured.'}`
          }
        ]
      },
      ...conversationForRequest.map((message) => ({
        role: message.role,
        parts: [{ text: message.content }]
      }))
    ];

    const body = {
      contents,
      generationConfig: {
        temperature: 0.5,
        topP: 0.8,
        topK: 32,
        maxOutputTokens: 512
      }
    };

    try {
      const response = await fetch(`${MODEL_ENDPOINT}?key=${GOOGLE_AI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error?.message || 'API error');
      }

      const parts = data?.candidates?.[0]?.content?.parts || [];
      let replyText = parts.map((part) => part.text).join('\n').trim();

      if (!replyText) {
        replyText =
          detectedLanguage === 'bg'
            ? 'Не успях да взема отговор. Попитай ме отново за продуктите или доставката.'
            : 'I could not get a reply. Please ask again about the products or shipping.';
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          content: replyText
        }
      ]);
    } catch (error) {
      const fallback =
        detectedLanguage === 'bg'
          ? 'В момента не мога да се свържа с модела. Най-важното: безплатна доставка при 2 или повече артикула в поръчката, иначе до 3.99 €. Можеш да вземеш комплект за теб и твоя gym buddy.'
          : 'I cannot reach the model right now. Key info: free delivery for 2+ items, otherwise up to 3.99 €. You can grab a set for you and your gym buddy.';
      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          content: fallback
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    void sendMessage();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void sendMessage();
    }
  };

  return (
    <div className="chatbot-widget" aria-live="polite">
      <button
        type="button"
        className={`chatbot-launcher${isOpen ? ' open' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Close straps assistant' : 'Open straps assistant'}
      >
        <span>AI</span>
      </button>

      {isOpen && (
        <div className="chatbot-window" role="dialog" aria-modal="false" aria-label="Straps assistant">
          <div className="chatbot-header">
            <div>
              <h3>Straps Assistant</h3>
            </div>
            <div className="chatbot-header-actions">
              <button
                type="button"
                className="chatbot-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close assistant"
              >
                ×
              </button>
            </div>
          </div>

          <div className="chatbot-messages" ref={messagesContainerRef}>
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`chatbot-message ${message.role}`}
              >
                {message.content}
              </div>
            ))}
            {isLoading && (
              <div className="chatbot-message model typing">
                Мисля…
              </div>
            )}
          </div>

          <form className="chatbot-input-area" onSubmit={handleSubmit}>
            <label htmlFor="chatbot-input-straps" className="visually-hidden">
              Ask a question
            </label>
            <textarea
              id="chatbot-input-straps"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Попитай за продукти, доставка или комплект за gym buddy..."
              rows={2}
              disabled={isLoading}
            />
            <button type="submit" disabled={isLoading || !inputValue.trim()}>
              {isLoading ? 'Изпращам…' : 'Изпрати'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatbotWidget;

